(function () {
  'use strict';

  // ─── State ────────────────────────────────────────────────────────────────
  const state = {
    allProducts: [],
    filteredProducts: [],
    selectedItems: [],     // {productId, variantId, title, vendor, price, image, variantTitle}
    currentPage: 1,
    perPage: 24,
    activeCollection: '',
    searchQuery: '',
    isLoading: false,
    isFetched: false
  };

  let config = {};
  let searchDebounceTimer = null;

  // ─── Init ─────────────────────────────────────────────────────────────────
  function init() {
    const configEl = document.getElementById('bundleConfig');
    if (!configEl) return;

    try {
      config = JSON.parse(configEl.textContent);
    } catch (e) {
      console.error('Bundle config parse error:', e);
      return;
    }

    state.perPage = config.perPage || 24;

    // Load filter collection membership from Liquid-rendered JSON
    const fcEl = document.getElementById('bundleFilterCollections');
    if (fcEl) {
      try {
        const fcs = JSON.parse(fcEl.textContent);
        config.filterCollectionSets = {};
        fcs.forEach(fc => {
          config.filterCollectionSets[fc.handle] = new Set(fc.productIds);
        });
      } catch (e) { config.filterCollectionSets = {}; }
    }

    setupEventListeners();
    setupMobileKeyboardDismiss();
    loadProducts();
    updateStickyBar();
    observeStickyVisibility();
  }

  // ─── Product Loading ───────────────────────────────────────────────────────
  async function loadProducts() {
    const cached = getCache(config.cacheKey);
    if (cached && cached.length > 0) {
      state.allProducts = cached;
      state.isFetched = true;
      applyFilters();
      return;
    }

    setLoading(true);

    const mainHandle = config.mainCollectionHandle;
    if (!mainHandle) {
      showError('No main collection configured. Please set one in the theme editor.');
      setLoading(false);
      return;
    }

    const productMap = new Map();

    for (const handle of [mainHandle]) {
      let page = 1;
      while (true) {
        try {
          const res = await fetch(`/collections/${handle}/products.json?limit=250&page=${page}`);
          if (!res.ok) break;
          const data = await res.json();
          const products = data.products || [];
          if (!products.length) break;

          products.forEach(p => {
            if (!productMap.has(p.id)) {
              productMap.set(p.id, normalizeProduct(p));
            }
          });

          if (products.length < 250) break;
          page++;
        } catch (err) {
          console.error('Fetch error:', err);
          break;
        }
      }
    }

    state.allProducts = Array.from(productMap.values());
    state.isFetched = true;
    setCache(config.cacheKey, state.allProducts);
    setLoading(false);
    applyFilters();
  }

  function normalizeProduct(p) {
    const img = p.images && p.images.length ? p.images[0].src : '';
    return {
      id: p.id,
      title: p.title,
      vendor: p.vendor || '',
      handle: p.handle,
      available: (p.variants || []).some(v => v.available),
      price: p.variants[0]?.price || 0,
      compare_at_price: p.variants[0]?.compare_at_price || 0,
      image: img ? img.replace(/(\.[^.]+)$/, '_medium$1') : '',
      has_only_default_variant: p.variants.length === 1 && p.variants[0].title === 'Default Title',
      options: p.options || [],
      variants: (p.variants || []).map(v => ({
        id: v.id,
        title: v.title,
        price: v.price,
        compare_at_price: v.compare_at_price,
        available: v.available,
        option1: v.option1,
        option2: v.option2,
        option3: v.option3,
        featured_image: v.featured_image ? v.featured_image.src : null
      }))
    };
  }

  // ─── Filtering & Search ────────────────────────────────────────────────────
  function applyFilters() {
    const q = state.searchQuery.toLowerCase().trim();
    const col = state.activeCollection;
    const colSet = col && config.filterCollectionSets ? config.filterCollectionSets[col] : null;

    state.filteredProducts = state.allProducts.filter(p => {
      const matchCollection = !col || (colSet && colSet.has(p.id));
      const matchSearch = !q || p.title.toLowerCase().includes(q) || p.vendor.toLowerCase().includes(q);
      return p.available && matchCollection && matchSearch;
    });

    state.currentPage = 1;
    renderGrid();
    renderPagination();
    updateResultsCount();
  }

  // ─── Rendering ────────────────────────────────────────────────────────────
  function renderGrid() {
    const skeleton = document.getElementById('bundleSkeletonGrid');
    if (skeleton) skeleton.style.display = 'none';

    const container = document.getElementById('bundleProductsContainer');
    if (!container) return;

    const start = (state.currentPage - 1) * state.perPage;
    const page = state.filteredProducts.slice(start, start + state.perPage);

    if (!page.length) {
      container.innerHTML = `<div class="bundle-empty">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ccc" stroke-width="1.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
        <p>No products found. Try a different search or filter.</p>
      </div>`;
      return;
    }

    container.innerHTML = page.map(p => renderProductCard(p)).join('');

    // Restore selected state
    state.selectedItems.forEach(item => {
      const card = container.querySelector(`[data-product-id="${item.productId}"]`);
      if (card) {
        card.classList.add('selected');
        syncVariantSelectToItem(card, item);
      }
    });

    attachCardListeners(container);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function renderProductCard(product) {
    const selectedItem = state.selectedItems.find(i => i.productId == product.id);
    const isSelected = !!selectedItem;

    // Find the first variant where no option value is excluded
    const firstNonExcluded = product.variants.find(v =>
      !['option1', 'option2', 'option3'].some(opt => v[opt] && isExcludedValue(v[opt]))
      && v.available
    ) || product.variants.find(v =>
      !['option1', 'option2', 'option3'].some(opt => v[opt] && isExcludedValue(v[opt]))
    ) || product.variants[0];

    const currentVariantId = selectedItem ? selectedItem.variantId : firstNonExcluded.id;
    const currentVariant = product.variants.find(v => v.id == currentVariantId) || firstNonExcluded;

    const price = parseFloat(currentVariant.price) || 0;
    const comparePrice = parseFloat(currentVariant.compare_at_price) || 0;
    const hasDiscount = comparePrice > price;
    const discountPct = hasDiscount ? Math.round(((comparePrice - price) / comparePrice) * 100) : 0;

    const variantOptions = product.has_only_default_variant ? '' : product.options.map((opt, i) => {
      // For options after the first, only show values available given current upstream selections
      let values;
      if (i === 0) {
        values = [...new Set(product.variants.map(v => v['option1']).filter(Boolean))];
      } else {
        const prevSelections = product.options.slice(0, i).map((_, j) => currentVariant[`option${j + 1}`]);
        const matching = product.variants.filter(v =>
          prevSelections.every((val, j) => v[`option${j + 1}`] === val)
        );
        values = [...new Set(matching.map(v => v[`option${i + 1}`]).filter(Boolean))];
      }
      // Remove excluded variant option values
      values = values.filter(val => !isExcludedValue(val));
      const labelHtml = opt.name === 'Quantity'
        ? `<span class="quantity-label">${opt.name}:</span>`
        : `${escapeHtml(opt.name)}:`;
      return `<div class="variant-option">
        <label>${labelHtml}</label>
        <select class="variant-select" data-option-index="${i}" data-option-name="${escapeAttr(opt.name)}" data-product-id="${product.id}">
          ${values.map(val => `<option value="${escapeAttr(val)}" ${currentVariant[`option${i + 1}`] === val ? 'selected' : ''}>${escapeHtml(val)}</option>`).join('')}
        </select>
      </div>`;
    }).join('');

    return `<div class="bundle-product-card${isSelected ? ' selected' : ''}"
        data-product-id="${product.id}"
        data-selected-variant-id="${currentVariantId}"
        data-product-title="${escapeAttr(product.title)}"
        data-product-vendor="${escapeAttr(product.vendor)}"
        data-product-image="${escapeAttr(product.image)}"
        data-variants='${escapeJson(product.variants)}'>

      <div class="selected-indicator">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="11" fill="#000" stroke="white" stroke-width="1.5"/>
          <path d="M7 12L10.5 15.5L17 9" stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>

      ${hasDiscount ? `<div class="card-discount-badge">-${discountPct}%</div>` : ''}

      <div class="product-image-wrapper">
        <img src="${product.image || 'data:image/svg+xml,%3Csvg xmlns=%22http://www.w3.org/2000/svg%22 width=%22200%22 height=%22200%22%3E%3C/svg%3E'}"
             alt="${escapeAttr(product.title)}"
             loading="lazy"
             class="product-img">
      </div>

      <div class="product-info">
        <div class="product-vendor">${escapeHtml(product.vendor)}</div>
        <h3 class="product-title">${escapeHtml(product.title)}</h3>

        ${variantOptions ? `<div class="variant-selector-wrapper">${variantOptions}</div>` : ''}

        <div class="product-price">
          ${hasDiscount
            ? `<span class="price-sale">AED ${Math.round(price)}</span>
               <span class="price-compare">AED ${Math.round(comparePrice)}</span>`
            : `<span class="price-sale">AED ${Math.round(price)}</span>`
          }
        </div>
      </div>

      <button type="button" class="select-product-btn">
        <span class="btn-text-select">+ Add to Bundle</span>
        <span class="btn-text-selected">✓ Added</span>
      </button>
    </div>`;
  }

  function renderPagination() {
    const container = document.getElementById('bundlePagination');
    if (!container) return;

    const totalPages = Math.ceil(state.filteredProducts.length / state.perPage);

    if (totalPages <= 1) {
      container.innerHTML = '';
      return;
    }

    const cur = state.currentPage;
    let pages = [];

    pages.push(1);
    if (cur > 3) pages.push('...');
    for (let i = Math.max(2, cur - 1); i <= Math.min(totalPages - 1, cur + 1); i++) {
      pages.push(i);
    }
    if (cur < totalPages - 2) pages.push('...');
    if (totalPages > 1) pages.push(totalPages);

    container.innerHTML = `
      <button class="page-btn prev-btn" data-page="${cur - 1}" ${cur === 1 ? 'disabled' : ''}>
        ← Prev
      </button>
      ${pages.map(p => p === '...'
        ? `<span class="page-ellipsis">…</span>`
        : `<button class="page-btn${p === cur ? ' active' : ''}" data-page="${p}">${p}</button>`
      ).join('')}
      <button class="page-btn next-btn" data-page="${cur + 1}" ${cur === totalPages ? 'disabled' : ''}>
        Next →
      </button>`;

    container.querySelectorAll('.page-btn:not([disabled])').forEach(btn => {
      btn.addEventListener('click', () => {
        state.currentPage = parseInt(btn.dataset.page);
        renderGrid();
        renderPagination();
      });
    });
  }

  function updateResultsCount() {
    const el = document.getElementById('bundleResultsCount');
    if (!el) return;
    const total = state.filteredProducts.length;
    const start = (state.currentPage - 1) * state.perPage + 1;
    const end = Math.min(state.currentPage * state.perPage, total);
    el.textContent = total === 0
      ? 'No products found'
      : `Showing ${start}–${end} of ${total} products`;
  }

  // ─── Card Listeners ────────────────────────────────────────────────────────
  function attachCardListeners(container) {
    container.querySelectorAll('.bundle-product-card').forEach(card => {
      card.querySelector('.select-product-btn')?.addEventListener('click', e => {
        e.stopPropagation();
        toggleSelection(card);
      });

      card.querySelectorAll('.variant-select').forEach((select, idx) => {
        select.addEventListener('change', e => {
          e.stopPropagation();
          handleVariantChange(card, select, idx);
        });
        select.addEventListener('click', e => e.stopPropagation());
      });

      card.addEventListener('click', e => {
        if (!e.target.closest('.variant-select') && !e.target.closest('.select-product-btn')) {
          toggleSelection(card);
        }
      });
    });
  }

  function handleVariantChange(card, changedSelect, changedIndex) {
    const productId = card.dataset.productId;
    const variants = JSON.parse(card.dataset.variants || '[]');
    const allSelects = card.querySelectorAll('.variant-select');

    // Cascade: rebuild downstream dropdowns based on new upstream selection
    updateDependentDropdowns(card, variants, allSelects, changedIndex);

    // Update custom labels (e.g. Travel Spray changes Quantity → Color)
    updateCustomLabels(card, allSelects);

    // Find the matching variant from all current selections
    const selectedOptions = Array.from(allSelects).map(s => s.value);
    const matched = variants.find(v =>
      selectedOptions.every((opt, i) => v[`option${i + 1}`] === opt)
    ) || variants[0];

    card.dataset.selectedVariantId = matched.id;

    const price = parseFloat(matched.price) || 0;
    const compare = parseFloat(matched.compare_at_price) || 0;
    const hasDiscount = compare > price;

    const priceEl = card.querySelector('.product-price');
    if (priceEl) {
      priceEl.innerHTML = hasDiscount
        ? `<span class="price-sale">AED ${Math.round(price)}</span><span class="price-compare">AED ${Math.round(compare)}</span>`
        : `<span class="price-sale">AED ${Math.round(price)}</span>`;
    }

    if (matched.featured_image) {
      const img = card.querySelector('.product-img');
      if (img) {
        img.style.opacity = '0.5';
        img.src = matched.featured_image;
        img.onload = () => { img.style.opacity = '1'; };
      }
    }

    // Update selected item if this product is already in the bundle
    const idx = state.selectedItems.findIndex(i => i.productId == productId);
    if (idx !== -1) {
      state.selectedItems[idx].variantId = matched.id;
      state.selectedItems[idx].price = parseFloat(matched.price) || 0;
      state.selectedItems[idx].variantTitle = matched.title !== 'Default Title' ? matched.title : '';
      if (matched.featured_image) state.selectedItems[idx].image = matched.featured_image;
      updateStickyBar();
    }
  }

  // Rebuild all dropdown options after the changed index, filtered by upstream selections
  function updateDependentDropdowns(card, variants, allSelects, changedIndex) {
    for (let nextIdx = changedIndex + 1; nextIdx < allSelects.length; nextIdx++) {
      const nextSelect = allSelects[nextIdx];
      const prevSelections = Array.from(allSelects).slice(0, nextIdx).map(s => s.value);

      // Find all variants matching the upstream selections
      const matching = variants.filter(v =>
        prevSelections.every((val, j) => v[`option${j + 1}`] === val)
      );

      // Collect unique values, preserving order, with availability info
      const seen = new Set();
      const availValues = [];
      matching.forEach(v => {
        const val = v[`option${nextIdx + 1}`];
        if (val && !seen.has(val)) {
          seen.add(val);
          const hasAvail = matching.some(mv => mv[`option${nextIdx + 1}`] === val && mv.available);
          availValues.push({ val, hasAvail });
        }
      });

      // Filter out excluded values
      const filteredValues = availValues.filter(({ val }) => !isExcludedValue(val));

      const currentVal = nextSelect.value;
      nextSelect.innerHTML = filteredValues.map(({ val, hasAvail }) =>
        `<option value="${escapeAttr(val)}"${!hasAvail ? ' disabled' : ''}>${escapeHtml(val)}${!hasAvail ? ' (Out of Stock)' : ''}</option>`
      ).join('');

      // Keep current value if still valid; otherwise browser auto-selects first option
      if (filteredValues.some(fv => fv.val === currentVal)) nextSelect.value = currentVal;
    }
  }

  // Update labels that depend on upstream variant selections (store-specific logic)
  function updateCustomLabels(card, allSelects) {
    allSelects.forEach(select => {
      if (select.dataset.optionName === 'Find the Fragrance') {
        const fragranceType = select.value || '';
        const quantityLabel = card.querySelector('.quantity-label');
        if (quantityLabel) {
          quantityLabel.textContent = fragranceType.toLowerCase().includes('travel spray')
            ? 'Color : 10 ML'
            : 'Quantity:';
        }
      }
    });
  }

  function syncVariantSelectToItem(card, item) {
    const variants = JSON.parse(card.dataset.variants || '[]');
    const variant = variants.find(v => v.id == item.variantId);
    if (!variant) return;
    card.querySelectorAll('.variant-select').forEach((sel, i) => {
      const val = variant[`option${i + 1}`];
      if (val) sel.value = val;
    });
  }

  // ─── Selection ─────────────────────────────────────────────────────────────
  function toggleSelection(card) {
    const isSelected = card.classList.contains('selected');
    if (isSelected) {
      removeFromSelection(card);
    } else {
      addToSelection(card);
    }
    updateStickyBar();
  }

  function addToSelection(card) {
    const productId = card.dataset.productId;
    if (state.selectedItems.find(i => i.productId == productId)) return;

    const variantId = card.dataset.selectedVariantId;
    const variants = JSON.parse(card.dataset.variants || '[]');
    const variant = variants.find(v => v.id == variantId) || variants[0];

    state.selectedItems.push({
      productId: parseInt(productId),
      variantId: variant ? variant.id : parseInt(productId),
      title: card.dataset.productTitle,
      vendor: card.dataset.productVendor,
      price: variant ? parseFloat(variant.price) : 0,
      image: (variant && variant.featured_image) ? variant.featured_image : card.dataset.productImage,
      variantTitle: (variant && variant.title !== 'Default Title') ? variant.title : ''
    });

    card.classList.add('selected');
  }

  function removeFromSelection(card) {
    const productId = card.dataset.productId;
    state.selectedItems = state.selectedItems.filter(i => i.productId != productId);
    card.classList.remove('selected');
  }

  // ─── Pricing ───────────────────────────────────────────────────────────────
  function calcPricing() {
    const items = state.selectedItems;
    const count = items.length;
    const freeCount = Math.floor(count / 3);

    const sorted = [...items].sort((a, b) => a.price - b.price);
    const freeItems = sorted.slice(0, freeCount);
    const totalBefore = items.reduce((s, i) => s + i.price, 0);
    const savings = freeItems.reduce((s, i) => s + i.price, 0);
    const totalAfter = totalBefore - savings;

    const toNextFree = count === 0
      ? 3
      : ((3 - (count % 3)) % 3) || 3;

    const posInSet = count % 3; // 0, 1, or 2

    return { count, freeCount, totalBefore, savings, totalAfter, toNextFree, posInSet };
  }

  // ─── Sticky Bar ────────────────────────────────────────────────────────────
  function updateStickyBar() {
    const pricing = calcPricing();
    const { count, freeCount, totalBefore, savings, totalAfter, toNextFree, posInSet } = pricing;

    // Count
    el('stickyCount', count);

    // Free badge
    const freeBadge = document.getElementById('stickyFreeBadge');
    if (freeBadge) {
      freeBadge.style.display = freeCount > 0 ? 'inline-flex' : 'none';
      el('stickyFreeCount', freeCount);
    }

    // Thumbnails
    renderThumbnails();

    // Progress dots
    const dots = document.querySelectorAll('.progress-dot');
    dots.forEach((dot, i) => {
      dot.classList.remove('filled', 'pulse');
      if (count > 0) {
        if (i < posInSet) dot.classList.add('filled');
        if (i === posInSet && posInSet < 3) dot.classList.add('pulse');
      }
    });

    // Progress text
    const progressText = document.getElementById('stickyProgressText');
    if (progressText) {
      if (count === 0) {
        progressText.textContent = 'Add 3 products to get 1 FREE';
      } else if (posInSet === 0 && count > 0) {
        progressText.innerHTML = `🎁 <strong>${freeCount} item${freeCount > 1 ? 's' : ''} FREE</strong> — add 3 more for another!`;
      } else {
        progressText.innerHTML = `Add <strong>${toNextFree}</strong> more to get another FREE item!`;
      }
    }

    // Prices
    const totalEl = document.getElementById('stickyTotalPrice');
    const origEl = document.getElementById('stickyOriginalPrice');
    const saveEl = document.getElementById('stickySaveAmount');

    if (totalEl) totalEl.textContent = `AED ${Math.round(totalAfter)}`;

    if (origEl) {
      if (savings > 0) {
        origEl.textContent = `AED ${Math.round(totalBefore)}`;
        origEl.style.display = 'inline';
      } else {
        origEl.style.display = 'none';
      }
    }

    if (saveEl) {
      if (savings > 0) {
        saveEl.textContent = `You save AED ${Math.round(savings)}`;
        saveEl.style.display = 'inline';
      } else {
        saveEl.style.display = 'none';
      }
    }

    // Discount percentage badge
    const pctEl = document.getElementById('stickySavePercent');
    if (pctEl) {
      if (config.showDiscountPercent && savings > 0 && totalBefore > 0) {
        const pct = Math.round((savings / totalBefore) * 100);
        pctEl.textContent = `${pct}% OFF`;
        pctEl.style.display = 'inline';
      } else {
        pctEl.style.display = 'none';
      }
    }

    // CTA button
    const btn = document.getElementById('stickyCtaBtn');
    const btnText = document.getElementById('stickyCtaText');
    if (btn && btnText) {
      const isMobile = window.innerWidth < 640;
      const price = `AED ${Math.round(totalAfter)}`;
      if (count === 0) {
        btn.disabled = true;
        btnText.textContent = 'Select products';
      } else if (posInSet === 0 && freeCount > 0) {
        btn.disabled = false;
        btnText.textContent = isMobile
          ? `🎁 ${freeCount} FREE — ${price}`
          : `🎁 Claim ${freeCount} FREE item${freeCount > 1 ? 's' : ''} — ${price}`;
      } else if (!isMobile && toNextFree <= 2 && count > 0) {
        btn.disabled = false;
        btnText.textContent = `Add to Cart — ${price}`;
      } else {
        btn.disabled = false;
        btnText.textContent = `Add to Cart — ${price}`;
      }
    }

    // Visibility
    const bar = document.getElementById('bundleStickyBar');
    if (bar) {
      bar.classList.toggle('has-selection', count > 0);
    }
  }

  function renderThumbnails() {
    const emptyEl = document.getElementById('stickyThumbsEmpty');
    const listEl = document.getElementById('stickyThumbsList');
    if (!listEl) return;

    if (state.selectedItems.length === 0) {
      if (emptyEl) emptyEl.style.display = 'flex';
      listEl.innerHTML = '';
      return;
    }

    if (emptyEl) emptyEl.style.display = 'none';

    const sorted = [...state.selectedItems].sort((a, b) => a.price - b.price);
    const freeCount = Math.floor(state.selectedItems.length / 3);

    listEl.innerHTML = sorted.map((item, i) => {
      const isFree = i < freeCount;
      return `<div class="sticky-thumb${isFree ? ' is-free' : ''}" title="${escapeAttr(item.title)}${isFree ? ' (FREE)' : ''}">
        <img src="${item.image || ''}" alt="${escapeAttr(item.title)}" loading="lazy">
        ${isFree ? '<span class="thumb-free-tag">FREE</span>' : ''}
        <button class="thumb-remove-btn" data-product-id="${item.productId}" aria-label="Remove">×</button>
      </div>`;
    }).join('');

    listEl.querySelectorAll('.thumb-remove-btn').forEach(btn => {
      btn.addEventListener('click', e => {
        e.stopPropagation();
        const productId = btn.dataset.productId;
        state.selectedItems = state.selectedItems.filter(i => i.productId != productId);
        const card = document.querySelector(`[data-product-id="${productId}"]`);
        if (card) card.classList.remove('selected');
        updateStickyBar();
      });
    });
  }

  // ─── Mobile Keyboard Dismiss on Scroll ───────────────────────────────────────
  function setupMobileKeyboardDismiss() {
    const searchInput = document.getElementById('bundleSearchInput');
    if (!searchInput) return;

    let isSearchFocused = false;
    let scrollStartY = 0;

    searchInput.addEventListener('focus', () => {
      isSearchFocused = true;
      scrollStartY = window.scrollY;
    });
    searchInput.addEventListener('blur', () => { isSearchFocused = false; });

    window.addEventListener('scroll', () => {
      if (isSearchFocused && Math.abs(window.scrollY - scrollStartY) > 60) {
        searchInput.blur();
      }
    }, { passive: true });
  }

  // ─── Sticky Visibility ─────────────────────────────────────────────────────
  function observeStickyVisibility() {
    const bar = document.getElementById('bundleStickyBar');
    if (!bar) return;

    const toggle = () => {
      const show = state.selectedItems.length > 0 || window.scrollY > 300;
      bar.classList.toggle('visible', show);
    };

    window.addEventListener('scroll', toggle, { passive: true });
    toggle();
  }

  // ─── Add to Cart ───────────────────────────────────────────────────────────
  async function handleAddToCart() {
    if (state.selectedItems.length === 0) return;

    const btn = document.getElementById('stickyCtaBtn');
    const loader = btn?.querySelector('.sticky-cta-loader');
    const text = document.getElementById('stickyCtaText');

    if (btn) btn.disabled = true;
    if (loader) loader.style.display = 'inline-block';
    if (text) text.textContent = 'Adding to cart…';

    const bundleId = `b2g1_${Date.now()}`;
    const items = state.selectedItems.map((item, idx) => ({
      id: item.variantId,
      quantity: 1,
      properties: {
        '_bundle_id': bundleId,
        '_bundle_pos': idx + 1,
        '_bundle_type': 'Buy 2 Get 1 Free'
      }
    }));

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || 'Failed to add to cart');
      }

      showToast('Added to cart! Discount applied at checkout. 🎉', 'success');
      await refreshCartCount();

      setTimeout(() => { window.location.href = '/cart'; }, 1200);

    } catch (err) {
      console.error('Cart error:', err);
      showToast(err.message || 'Could not add to cart. Please try again.', 'error');
      if (btn) btn.disabled = false;
    } finally {
      if (loader) loader.style.display = 'none';
      updateStickyBar();
    }
  }

  async function refreshCartCount() {
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      document.querySelectorAll('[data-cart-count], .cart-count').forEach(el => {
        el.textContent = cart.item_count;
        el.setAttribute('data-cart-count', cart.item_count);
      });
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
    } catch (e) {}
  }

  // ─── Event Listeners ───────────────────────────────────────────────────────
  function setupEventListeners() {
    // Search
    const searchInput = document.getElementById('bundleSearchInput');
    const searchClear = document.getElementById('bundleSearchClear');

    searchInput?.addEventListener('input', e => {
      const val = e.target.value;
      if (searchClear) searchClear.style.display = val ? 'flex' : 'none';
      clearTimeout(searchDebounceTimer);
      searchDebounceTimer = setTimeout(() => {
        state.searchQuery = val;
        applyFilters();
      }, 280);
    });

    searchClear?.addEventListener('click', () => {
      if (searchInput) searchInput.value = '';
      searchClear.style.display = 'none';
      state.searchQuery = '';
      applyFilters();
    });

    // Collection filter pills
    document.querySelectorAll('.filter-pill').forEach(pill => {
      pill.addEventListener('click', () => {
        document.querySelectorAll('.filter-pill').forEach(p => p.classList.remove('active'));
        pill.classList.add('active');
        state.activeCollection = pill.dataset.collection || '';
        applyFilters();
      });
    });

    // CTA button
    document.getElementById('stickyCtaBtn')?.addEventListener('click', handleAddToCart);
  }

  // ─── Loading State ─────────────────────────────────────────────────────────
  function setLoading(loading) {
    state.isLoading = loading;
    const skeleton = document.getElementById('bundleSkeletonGrid');
    const container = document.getElementById('bundleProductsContainer');
    if (skeleton) skeleton.style.display = loading ? 'grid' : 'none';
    if (container) container.style.display = loading ? 'none' : '';
  }

  function showError(msg) {
    const container = document.getElementById('bundleProductsContainer');
    if (container) {
      container.innerHTML = `<div class="bundle-empty"><p>${escapeHtml(msg)}</p></div>`;
    }
  }

  // ─── Toast ────────────────────────────────────────────────────────────────
  function showToast(msg, type = 'info') {
    const toast = document.createElement('div');
    toast.className = `bundle-toast bundle-toast--${type}`;
    toast.textContent = msg;
    document.body.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('visible'));
    setTimeout(() => {
      toast.classList.remove('visible');
      setTimeout(() => toast.remove(), 400);
    }, 3500);
  }

  // ─── Excluded Variant Helper ──────────────────────────────────────────────
  function isExcludedValue(val) {
    const excluded = (config.excludedVariants || []).map(v => v.toLowerCase().trim()).filter(Boolean);
    if (!excluded.length || !val) return false;
    return excluded.some(ex => val.toLowerCase().includes(ex));
  }

  // ─── Session Cache ─────────────────────────────────────────────────────────
  function getCache(key) {
    try {
      const raw = sessionStorage.getItem(key);
      if (!raw) return null;
      const { ts, data } = JSON.parse(raw);
      if (Date.now() - ts > 15 * 60 * 1000) return null; // 15 min TTL
      return data;
    } catch (e) { return null; }
  }

  function setCache(key, data) {
    try {
      sessionStorage.setItem(key, JSON.stringify({ ts: Date.now(), data }));
    } catch (e) {}
  }

  // ─── Helpers ──────────────────────────────────────────────────────────────
  function el(id, text) {
    const e = document.getElementById(id);
    if (e) e.textContent = text;
  }

  function escapeHtml(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function escapeAttr(str) {
    return String(str).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function escapeJson(obj) {
    return JSON.stringify(obj).replace(/'/g, '&#39;').replace(/"/g, '&quot;');
  }

  // ─── Boot ─────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
